import { useState, useContext } from 'react';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  Divider,
  useTheme,
  IconButton,
  useMediaQuery,
  ListItemText,
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import { bloxValues } from 'themes/constants';
import { SearchContext } from '../SearchContextProvider';

export type SortOption = {
  label: string;
  field: string;
};

export type SortDropdownProps = {
  sortOptions: SortOption[];
  /** default true - should ASC/DESC dir be uppercase? */
  uppercaseDir?: boolean;
  /* default true - for saved search contexts, will add reloadDefault state to location nav state */
  withReloadDefaultState?: boolean;
  /** Pass queryParams directly (if not using SearchContextProvider) */
  queryParams?: URLSearchParams;
  /** Pass updateQueryParams directly (if not using SearchContextProvider) */
  updateQueryParams?: (updater: (prevParams: URLSearchParams) => URLSearchParams) => void;
};

const SortDropdown = ({
  sortOptions,
  uppercaseDir = true,
  withReloadDefaultState = true,
  queryParams: propsQueryParams,
  updateQueryParams: propsUpdateQueryParams,
}: SortDropdownProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Optional search context
  const searchContext = useContext(SearchContext);

  // Use props if provided, otherwise fall back to context
  const queryParams = propsQueryParams || searchContext?.queryParams;
  const updateQueryParams = propsUpdateQueryParams || searchContext?.updateQueryParams;

  if (!queryParams || !updateQueryParams) {
    throw new Error(
      'SortDropdown requires either queryParams/updateQueryParams props or SearchContextProvider',
    );
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'sort-popover' : undefined;
  const currentField = queryParams.get('sort');
  const currentDir = queryParams.get('dir')?.toUpperCase() || 'DESC'; // Default to DESC if not set
  const currentOption = sortOptions.find((option) => option.field === currentField);
  const sortButtonText =
    currentField && currentOption
      ? `Sorted: ${currentOption.label} (${
          currentDir === 'ASC' ? 'Ascending' : 'Descending'
        })`
      : 'Sort';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSortChange = (field: string, direction: 'ASC' | 'DESC') => {
    const sortedDir = uppercaseDir ? direction : direction.toLowerCase(); // Handle case sensitivity
    updateQueryParams((prevParams) => {
      const params = new URLSearchParams(prevParams);
      params.set('sort', field);
      params.set('dir', sortedDir);
      return params;
    });
    handleClose();
  };

  const handleClearSort = () => {
    updateQueryParams((prevParams) => {
      const params = new URLSearchParams(prevParams);
      params.delete('sort');
      params.delete('dir');
      return params;
    });
    handleClose();
  };

  return (
    <>
      {isMobileOrTablet ? (
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label="open-sort"
          data-testid="sort-dropdown-button"
          aria-describedby={id}
          color={currentField ? 'primary' : undefined}
          sx={{ color: currentField ? undefined : theme.palette.text.secondary }}
        >
          <SortIcon
            fontSize="small"
            sx={{
              transition: 'transform 0.3s',
              transform: currentDir === 'ASC' ? 'rotateX(180deg)' : 'rotateX(0deg)',
            }}
          />
        </IconButton>
      ) : (
        <Button
          aria-describedby={id}
          onClick={handleClick}
          color={currentField ? 'primary' : undefined}
          size="small"
          sx={{
            color: currentField ? undefined : theme.palette.text.secondary,
            fontSize: bloxValues.smallFontSizeRem,
            '& .MuiButton-icon': {
              m: 0,
              ml: 0.5,
            },
          }}
          endIcon={
            <SortIcon
              sx={{
                transition: 'transform 0.3s',
                transform: currentDir === 'ASC' ? 'rotateX(180deg)' : 'rotateX(0deg)',
                fontSize: 'inherit',
              }}
            />
          }
        >
          {sortButtonText}
        </Button>
      )}
      <Menu
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        elevation={3}
        marginThreshold={2}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          list: { dense: true },
        }}
      >
        {sortOptions.map((option, idx) => [
          <MenuItem
            key={`${option?.field ?? idx}-asc`}
            onClick={() => handleSortChange(option?.field, 'ASC')}
            selected={currentField === option?.field && currentDir === 'ASC'}
          >
            <ListItemText primary={`${option?.label} - Ascending`} />
          </MenuItem>,

          <MenuItem
            key={`${option?.field ?? idx}-desc`}
            onClick={() => handleSortChange(option?.field, 'DESC')}
            selected={currentField === option?.field && currentDir === 'DESC'}
          >
            <ListItemText primary={`${option?.label} - Descending`} />
          </MenuItem>,
        ])}

        {currentField && [
          <Divider key="sort-divider" />,
          <MenuItem
            key="sort-clear"
            onClick={handleClearSort}
          >
            <ListItemText primary="Remove Sort" />
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
};

export default SortDropdown;
